import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { AdminPageHeader } from '../../components';
import { RevoltSwitch } from '../../../../components';
import { logger } from '../../../../core';
import { envUtils } from '../../../../core/utils';

export function AdminPushNotificationsSection() {
  const navigate = useNavigate();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [canReactivate, setCanReactivate] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    user_registration: true,
    plan_purchase: true,
    product_purchase: true,
    system_alerts: true,
    test_alerts: false
  });
  const [settingsLoading, setSettingsLoading] = useState(false);


  useEffect(() => {
    // Check if user should use mobile UI (combines device type + screen size)
    const checkMobile = () => {
      const uiMode = envUtils.device.getRecommendedUIMode();
      const mobile = uiMode === 'mobile';
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Check current subscription status
    checkSubscriptionStatus();

    return () => window.removeEventListener('resize', checkMobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSubscriptionStatus = useCallback(async () => {
    try {
      // Try to fetch notification settings - if successful, user is subscribed
      const settingsResponse = await fetch('/api/push/settings', {
        credentials: 'include'
      });

      const settingsData = await settingsResponse.json();

      if (settingsData.success) {
        if (settingsData.hasActiveSubscription) {
          setIsSubscribed(true);
          setCanReactivate(false);
          setNotificationSettings(settingsData.settings);
        } else {
          setIsSubscribed(false);
          setCanReactivate(settingsData.canReactivate || false);
        }
        return;
      }

      // If settings API fails, check admin status as fallback
      const adminResponse = await fetch('/api/push/admin-check', {
        credentials: 'include'
      });

      const adminData = await adminResponse.json();

      if (adminData.success) {
        // Admin status checked successfully - user is not subscribed yet
        setIsSubscribed(false);
      } else {
        logger.error('admin|push', 'Error checking status:', adminData.error);
        setIsSubscribed(false);
      }
    } catch (error) {
      logger.error({
        tags: 'admin|push',
        color1: 'red'
      }, 'Error checking subscription status:', error);
      setIsSubscribed(false);
    }
  }, []);

  const handleUnsubscribe = async () => {
    setIsUnsubscribing(true);

    try {
      const response = await fetch('/api/push/unsubscribe-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setIsSubscribed(false);
        setCanReactivate(true); // Now they can reactivate
        // Reset settings to defaults
        setNotificationSettings({
          user_registration: true,
          plan_purchase: true,
          product_purchase: true,
          system_alerts: true,
          test_alerts: false
        });
        logger.log({
          tags: 'admin|push|success',
          color1: 'green'
        }, 'Successfully unsubscribed from push notifications');
      } else {
        logger.error({
          tags: 'admin|push|error',
          color1: 'red'
        }, 'Error unsubscribing:', data.error);
      }
    } catch (error) {
      logger.error({
        tags: 'admin|push|error',
        color1: 'red'
      }, 'Error unsubscribing from push notifications:', error);
    } finally {
      setIsUnsubscribing(false);
    }
  };

  const handleSubscribe = async () => {
    if (!isMobile) {
      // Should not reach here due to UI logic, but safety check
      return;
    }

    setIsSubscribing(true);

    try {
      // Check if push notifications are supported
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        logger.error('admin|push', 'Push notifications not supported on this device');
        return;
      }

      // Check current permission status
      const permission = Notification.permission;

      if (permission === 'denied') {
        logger.error('admin|push', 'Notification permissions blocked');
        return;
      }

      // Request permission if not granted
      let finalPermission = permission;
      if (permission === 'default') {
        finalPermission = await Notification.requestPermission();
      }

      if (finalPermission !== 'granted') {
        logger.error('admin|push', 'Notification permission denied');
        return;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');

      // Get VAPID public key
      const keyResponse = await fetch('/api/push/vapid-public-key', {
        credentials: 'include'
      });

      if (!keyResponse.ok) {
        throw new Error('Failed to get VAPID public key');
      }

      const keyData = await keyResponse.json();
      if (!keyData.success || !keyData.publicKey) {
        throw new Error('Invalid VAPID public key response');
      }

      // Create push subscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyData.publicKey)
      });

      // Send to backend
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ subscription })
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription');
      }

      setIsSubscribed(true);
      setCanReactivate(false);

      // Fetch the settings that were created with defaults
      await fetchNotificationSettings();

      logger.log({
        tags: 'admin|push|success',
        color1: 'green'
      }, 'Admin subscribed to push notifications');

    } catch (error) {
      logger.error({
        tags: 'admin|push|error',
        color1: 'red'
      }, 'Error subscribing to push notifications:', error);
    } finally {
      setIsSubscribing(false);
    }
  };

  const fetchNotificationSettings = useCallback(async () => {
    try {
      const response = await fetch('/api/push/settings', {
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setNotificationSettings(data.settings);
      } else {
        logger.error({
          tags: 'admin|push|settings',
          color1: 'red'
        }, 'Error fetching notification settings:', data.error);
      }
    } catch (error) {
      logger.error({
        tags: 'admin|push|settings',
        color1: 'red'
      }, 'Error fetching notification settings:', error);
    }
  }, []);

  const updateNotificationSetting = async (settingKey, enabled) => {
    setSettingsLoading(true);

    try {
      const response = await fetch('/api/push/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          [settingKey]: enabled
        })
      });

      const data = await response.json();

      if (data.success) {
        setNotificationSettings(prev => ({
          ...prev,
          [settingKey]: enabled
        }));

        logger.log({
          tags: 'admin|push|settings',
          color1: 'green'
        }, `Updated ${settingKey} setting to ${enabled}`);
      } else {
        logger.error({
          tags: 'admin|push|settings',
          color1: 'red'
        }, 'Error updating notification setting:', data.error);
      }
    } catch (error) {
      logger.error({
        tags: 'admin|push|settings',
        color1: 'red'
      }, 'Error updating notification setting:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const outputArray = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
      outputArray[i] = raw.charCodeAt(i);
    }
    return outputArray;
  };

  const handleSimulateUserRegistration = async () => {
    setIsSimulating(true);

    try {
      // First, send the test notification through the normal system (respects preferences)
      const notificationResponse = await fetch('/api/push/notify-admins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          eventType: 'test_alerts',
          title: '👤 New User Registered',
          body: 'testuser@example.com (Test User) has joined Revolt',
          url: '/admin/users',
          data: {
            action: 'highlight_user',
            userId: 'test-user-123',
            email: 'testuser@example.com',
            username: 'Test User',
            priority: 'normal'
          }
        })
      });

      const notificationResult = await notificationResponse.json();
      if (!notificationResult.success) {
        throw new Error(notificationResult.error || 'Failed to send test notification');
      }

      // Store payload for users page (for the modal)
      const payloadData = {
        payload: {
          action: 'highlight_user',
          userId: 'test-user-123',
          email: 'testuser@example.com',
          username: 'Test User',
          priority: 'normal',
          fromNotification: true,
          clickedAt: Date.now()
        },
        timestamp: Date.now(),
        sourceSection: 'admin/push-notifications'
      };

      sessionStorage.setItem('sectionPayload_admin/users', JSON.stringify(payloadData));
      logger.log({
        tags: 'admin|push|simulation',
        color1: 'green'
      }, 'PushNotifications: Sent test notification and stored payload');

      // Navigate to users page
      await navigate('/admin/users');

    } catch (error) {
      logger.error({
        tags: 'admin|push|simulation',
        color1: 'red'
      }, 'Error simulating user registration:', error);
    } finally {
      setIsSimulating(false);
    }
  };

  const handleSimulatePlanPurchase = async () => {
    setIsSimulating(true);

    try {
      // First, send the test notification through the normal system (respects preferences)
      const notificationResponse = await fetch('/api/push/notify-admins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          eventType: 'test_alerts',
          title: '💳 Plan Purchased',
          body: 'User test-user-123 purchased Max Plan (Enhanced)',
          url: '/admin/users',
          data: {
            action: 'highlight_user',
            userId: 'test-user-123',
            planId: 'max',
            tierId: 'enhanced',
            billingCycle: 'annual',
            amount: 9999,
            priority: 'normal'
          }
        })
      });

      const notificationResult = await notificationResponse.json();
      if (!notificationResult.success) {
        throw new Error(notificationResult.error || 'Failed to send test notification');
      }

      // Store payload for users page (for the modal)
      const payloadData = {
        payload: {
          action: 'highlight_user',
          userId: 'test-user-123',
          planId: 'max',
          tierId: 'enhanced',
          billingCycle: 'annual',
          amount: 9999,
          priority: 'normal',
          fromNotification: true,
          clickedAt: Date.now()
        },
        timestamp: Date.now(),
        sourceSection: 'admin/push-notifications'
      };

      sessionStorage.setItem('sectionPayload_admin/users', JSON.stringify(payloadData));
      logger.log({
        tags: 'admin|push|simulation',
        color1: 'green'
      }, 'PushNotifications: Sent test notification and stored payload');

      // Navigate to users page
      await navigate('/admin/users');

    } catch (error) {
      logger.error({
        tags: 'admin|push|simulation',
        color1: 'red'
      }, 'Error simulating plan purchase:', error);
    } finally {
      setIsSimulating(false);
    }
  };

  const handleSimulateProductPurchase = async () => {
    setIsSimulating(true);

    try {
      const notificationResponse = await fetch('/api/push/notify-admins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          eventType: 'test_alerts',
          title: '🛒 Product Purchased',
          body: 'testuser@example.com purchased Example Product',
          url: '/admin/users',
          data: {
            action: 'highlight_product_purchase',
            userId: 'test-user-123',
            username: 'Test User',
            email: 'testuser@example.com',
            productId: 'test-product-123',
            productName: 'Example Product',
            amount: 2999,
            priority: 'normal'
          }
        })
      });

      const notificationResult = await notificationResponse.json();
      if (!notificationResult.success) {
        throw new Error(notificationResult.error || 'Failed to send test notification');
      }

      const payloadData = {
        payload: {
          action: 'highlight_product_purchase',
          userId: 'test-user-123',
          username: 'Test User',
          email: 'testuser@example.com',
          productId: 'test-product-123',
          productName: 'Example Product',
          amount: 2999,
          priority: 'normal',
          fromNotification: true,
          clickedAt: Date.now()
        },
        timestamp: Date.now(),
        sourceSection: 'admin/push-notifications'
      };

      sessionStorage.setItem('sectionPayload_admin/users', JSON.stringify(payloadData));
      logger.log({
        tags: 'admin|push|simulation',
        color1: 'green'
      }, 'PushNotifications: Sent product purchase test notification and stored payload');

      await navigate('/admin/users');

    } catch (error) {
      logger.error({
        tags: 'admin|push|simulation',
        color1: 'red'
      }, 'Error simulating product purchase:', error);
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <MainLayout contentClassName="main-content--admin">
      <AdminPageHeader
        title="Push Notifications"
        description="Manage push notification subscriptions for admin alerts"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Push Notifications', path: '/admin/push-notifications' }
        ]}
      />

      <div className="admin-push-notifications-container">
        <div className="admin-section-content">
          <div className="push-notifications-container">
            {/* Simple subscription card */}
            <div className="push-subscription-section">
              <h3>Push Notifications</h3>

              {isMobile ? (
                <div className="subscription-controls">
                  <button
                    className={`admin-btn ${isSubscribed ? 'danger' :
                        canReactivate ? 'secondary' : 'primary'
                      } ${(isSubscribing || isUnsubscribing) ? 'loading' : ''}`}
                    onClick={
                      isSubscribed ? handleUnsubscribe :
                        handleSubscribe
                    }
                    disabled={isSubscribing || isUnsubscribing}
                  >
                    {isSubscribing ? (
                      <>
                        <span className="spinner"></span>
                        {canReactivate ? 'Reactivating...' : 'Subscribing...'}
                      </>
                    ) : isUnsubscribing ? (
                      <>
                        <span className="spinner"></span>
                        Unsubscribing...
                      </>
                    ) : isSubscribed ? (
                      <>
                        <span className="x-icon">✕</span>
                        Unsubscribe
                      </>
                    ) : canReactivate ? (
                      <>
                        <span className="refresh-icon">🔄</span>
                        Reactivate Push Notifications
                      </>
                    ) : (
                      <>
                        <span className="bell-icon">🔔</span>
                        Enable Push Notifications
                      </>
                    )}
                  </button>

                  {isSubscribed && !isUnsubscribing && (
                    <p className="success-message">
                      ✅ You are now subscribed to push notifications. You'll receive alerts on this device when admin events occur.
                    </p>
                  )}
                </div>
              ) : (
                <div className="mobile-required-notice">
                  <p>📱 Push notifications are only available on mobile devices.</p>
                  <p>Please access this page from your mobile device to enable push notifications.</p>
                </div>
              )}
            </div>

            {isSubscribed && (
              <div className="push-settings-section">
                <h3>Notification Preferences</h3>
                <p>Choose which types of notifications you want to receive on your devices:</p>

                <div className="notification-settings">
                  <div className="setting-item">
                    <RevoltSwitch
                      id="user-registration-setting"
                      checked={notificationSettings.user_registration}
                      onChange={(checked) => updateNotificationSetting('user_registration', checked)}
                      disabled={settingsLoading}
                      label="👤 New User Registrations"
                    />
                    <p className="setting-description">
                      Get notified when new users register for the platform
                    </p>
                  </div>

                  <div className="setting-item">
                    <RevoltSwitch
                      id="plan-purchase-setting"
                      checked={notificationSettings.plan_purchase}
                      onChange={(checked) => updateNotificationSetting('plan_purchase', checked)}
                      disabled={settingsLoading}
                      label="💳 Plan Purchases"
                    />
                    <p className="setting-description">
                      Get notified when users purchase or upgrade plans
                    </p>
                  </div>

                  <div className="setting-item">
                    <RevoltSwitch
                      id="product-purchase-setting"
                      checked={notificationSettings.product_purchase}
                      onChange={(checked) => updateNotificationSetting('product_purchase', checked)}
                      disabled={settingsLoading}
                      label="🛒 Product Purchases"
                    />
                    <p className="setting-description">
                      Get notified when users purchase products from the marketplace
                    </p>
                  </div>

                  <div className="setting-item">
                    <RevoltSwitch
                      id="system-alerts-setting"
                      checked={notificationSettings.system_alerts}
                      onChange={(checked) => updateNotificationSetting('system_alerts', checked)}
                      disabled={settingsLoading}
                      label="🚨 System Alerts"
                    />
                    <p className="setting-description">
                      Get notified about important system events and issues
                    </p>
                  </div>

                  <div className="setting-item">
                    <RevoltSwitch
                      id="test-alerts-setting"
                      checked={notificationSettings.test_alerts}
                      onChange={(checked) => updateNotificationSetting('test_alerts', checked)}
                      disabled={settingsLoading}
                      label="🧪 Test Alerts"
                    />
                    <p className="setting-description">
                      Get notified when test notifications are sent from the dev tools
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="push-simulation-section">
              <h3>Test Notification Modals</h3>
              <p>Simulate receiving notifications to test the modal display on the users page:</p>

              <div className="simulation-buttons">
                <button
                  onClick={handleSimulateUserRegistration}
                  disabled={isSimulating}
                  className="simulation-btn user-registration-btn"
                >
                  {isSimulating ? '🔄 Simulating...' : '👤 Simulate User Registration'}
                </button>

                <button
                  onClick={handleSimulatePlanPurchase}
                  disabled={isSimulating}
                  className="simulation-btn plan-purchase-btn"
                >
                  {isSimulating ? '🔄 Simulating...' : '💳 Simulate Plan Purchase'}
                </button>

                <button
                  onClick={handleSimulateProductPurchase}
                  disabled={isSimulating}
                  className="simulation-btn product-purchase-btn"
                >
                  {isSimulating ? '🔄 Simulating...' : '🛒 Simulate Product Purchase'}
                </button>
              </div>

              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '12px' }}>
                Clicking these buttons will send a test notification and navigate to the users or products page with test payload data.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .admin-push-notifications-container {
          padding: 20px;
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          min-height: 0;
        }

        .admin-section-content {
          background: rgba(20, 20, 20, 0.5);
          border-radius: 12px;
          padding: 24px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .push-notifications-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .push-subscription-section,
        .push-settings-section,
        .push-simulation-section {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .push-subscription-section h3,
        .push-settings-section h3,
        .push-simulation-section h3 {
          color: #fff;
          margin: 0 0 15px 0;
          font-size: 1.2rem;
        }

        .push-subscription-section p,
        .push-settings-section > p,
        .push-simulation-section p {
          color: #ccc;
          line-height: 1.5;
          margin-bottom: 15px;
        }

        .subscription-controls {
          text-align: center;
        }


        .subscription-controls {
          text-align: center;
        }

        .admin-btn {
          background: #8b0000;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }

        .admin-btn:hover:not(:disabled) {
          background: #a00000;
          transform: translateY(-1px);
        }

        .admin-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .admin-btn.success {
          background: #16a34a;
        }

        .admin-btn.success:hover:not(:disabled) {
          background: #15803d;
        }

        .admin-btn.danger {
          background: #dc2626;
        }

        .admin-btn.danger:hover:not(:disabled) {
          background: #b91c1c;
        }

        .admin-btn.secondary {
          background: #6b7280;
        }

        .admin-btn.secondary:hover:not(:disabled) {
          background: #4b5563;
        }

        .admin-btn.loading {
          background: #374151;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .checkmark {
          color: #4ade80;
          font-size: 1.2rem;
        }

        .bell-icon {
          font-size: 1.1rem;
        }

        .x-icon {
          font-size: 1.1rem;
        }

        .refresh-icon {
          font-size: 1.1rem;
        }

        .success-message {
          color: #4ade80;
          font-weight: 500;
          margin-top: 15px;
          padding: 12px;
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 6px;
        }

        .mobile-required-notice {
          text-align: center;
          padding: 20px;
          background: rgba(245, 158, 11, 0.1);
          border: 1px solid rgba(245, 158, 11, 0.3);
          border-radius: 6px;
        }

        .mobile-required-notice p {
          color: #f59e0b;
          margin: 0 0 10px 0;
          font-weight: 500;
        }

        .mobile-required-notice p:last-child {
          margin-bottom: 0;
          font-size: 0.9rem;
          color: #d97706;
        }


        .push-simulation-section {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .push-simulation-section h3 {
          color: #fff;
          margin: 0 0 15px 0;
          font-size: 1.2rem;
        }

        .push-simulation-section p {
          color: #ccc;
          line-height: 1.5;
          margin-bottom: 15px;
        }

        .simulation-buttons {
          display: flex;
          gap: 12px;
          margin-top: 15px;
        }

        .simulation-btn {
          flex: 1;
          padding: 12px 16px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .simulation-btn:not(:disabled):hover {
          transform: translateY(-1px);
        }

        .simulation-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .user-registration-btn {
          background: #28a745;
          color: white;
        }

        .user-registration-btn:not(:disabled):hover {
          background: #218838;
        }

        .plan-purchase-btn {
          background: #007bff;
          color: white;
        }

        .plan-purchase-btn:not(:disabled):hover {
          background: #0056b3;
        }

        .push-settings-section {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .push-settings-section h3 {
          color: #fff;
          margin: 0 0 15px 0;
          font-size: 1.2rem;
        }

        .push-settings-section > p {
          color: #ccc;
          margin-bottom: 20px;
        }

        .notification-settings {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .setting-item {
          padding: 16px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .setting-item .revolt-switch {
          margin-bottom: 8px;
        }

        .setting-description {
          color: #999;
          font-size: 0.9rem;
          margin: 0;
          margin-left: 54px; /* Align with switch label */
        }
      `}</style>
    </MainLayout>
  );
}